import { FormGroup, InputGroup } from "@blueprintjs/core"
import CustomDialog from "components/Atoms/CustomDialog"
import DynamicSelector from "components/Molecules/DynamicSelector"
import { useShipmentPlanBySoNoQuery } from "generated/graphql"
import React, { useEffect, useState } from "react"
import {
  PRIMARY_PACK_MATERIAL,
  SECONDARY_PACK_MATERIAL,
} from "graphql/selector.queries.gql"
import ButtonMutation from "components/Molecules/ButtonMutation"
import { CREATE_UPDATE_SALES_ORDER } from "graphql/production/salesOrder.mutation.gql"

const PalletPackagingAction = ({
  isOpen,
  onClose,
  soId,
}: {
  isOpen: boolean
  onClose: () => void
  soId: string
}) => {
  const [formData, setFormData] = useState({
    id: "",
    primaryPackMaterial: null,
    secondaryPackMaterial: null,
    labels: "",
  })

  const {
    data: sData,
    refetch,
    loading,
    error,
  } = useShipmentPlanBySoNoQuery({
    variables: {
      soNo: soId,
    },
    fetchPolicy: "network-only",
    skip: !soId || !isOpen,
  })

  const salesOrder = sData?.shipmentPlanById

  // Pre-populate form when data loads
  useEffect(() => {
    if (salesOrder) {
      setFormData({
        id: salesOrder.id,
        primaryPackMaterial: salesOrder.primaryPackMaterial || null,
        secondaryPackMaterial: salesOrder.secondaryPackMaterial || null,
        labels: salesOrder.labels || "",
      })
    }
  }, [salesOrder, sData])

  if (loading) {
    return (
      <CustomDialog
        width="55%"
        icon="document-share"
        title="Edit Sales Order"
        isOpen={isOpen}
        onClose={onClose}
      >
        <div className="p-8 text-center">Loading...</div>
      </CustomDialog>
    )
  }

  if (error) {
    return (
      <CustomDialog
        width="55%"
        icon="document-share"
        title="Edit Sales Order"
        isOpen={isOpen}
        onClose={onClose}
      >
        <div className="p-8 text-center text-red-600">
          Error loading sales order: {error.message}
        </div>
      </CustomDialog>
    )
  }

  return (
    <CustomDialog
      width="55%"
      fixedHeader
      icon="document-share"
      title={`Edit Pallet - ${formData.id}`}
      isOpen={isOpen}
      onClose={onClose}
      isCloseButtonShown={false}
      footerRightButton={
        <div className="flex items-center justify-end w-full">
          <ButtonMutation
            mutation={CREATE_UPDATE_SALES_ORDER}
            variables={{
              input: {
                id: formData.id,
                primaryPackMaterialId: formData.primaryPackMaterial?.id,
                secondaryPackMaterialId: formData.secondaryPackMaterial?.id,
                labels: formData.labels,
                soNo: salesOrder?.soNo,
                weeklySrNo: salesOrder?.weeklySrNo,
                skuTypeId: salesOrder?.skuType?.id,
                exportCustomerId: salesOrder?.exportCustomer?.id,
              },
            }}
            onCompleted={() => {
              onClose()
            }}
            onError={error => {
              console.error("Error updating sales order:", error)
            }}
            text="Update"
            dark={false}
            intent="success"
          />
        </div>
      }
    >
      <div className="bg-white rounded-lg p-6">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-6">
            <FormGroup
              label={
                <>
                  Primary Pack Material <span className="text-red-600">*</span>
                </>
              }
            >
              <DynamicSelector
                query={PRIMARY_PACK_MATERIAL}
                value={formData.primaryPackMaterial}
                onChange={(_, value) =>
                  setFormData({ ...formData, primaryPackMaterial: value })
                }
                placeholder="Select primary packing"
              />
            </FormGroup>
          </div>

          <div className="col-span-6">
            <FormGroup
              label={
                <>
                  Secondary Pack Material{" "}
                  <span className="text-red-600">*</span>
                </>
              }
            >
              <DynamicSelector
                query={SECONDARY_PACK_MATERIAL}
                value={formData.secondaryPackMaterial}
                onChange={(_, value) =>
                  setFormData({ ...formData, secondaryPackMaterial: value })
                }
                placeholder="Select secondary packing"
              />
            </FormGroup>
          </div>

          <div className="col-span-6">
            <FormGroup label="Labels">
              <InputGroup
                value={formData.labels}
                onChange={e =>
                  setFormData({ ...formData, labels: e.target.value })
                }
                placeholder="Enter labels"
              />
            </FormGroup>
          </div>
        </div>
      </div>
    </CustomDialog>
  )
}

export default PalletPackagingAction
